import { create } from 'zustand';
import { EntityName } from 'shared/domain/entityName';
import { processClient } from 'shared/domain/process/processClient';
import type { ProcessType } from 'shared/domain/process/types/model';
import { createSelectors, StoreName, withDevtools } from '../config';
import { createEntityCollectionActions } from '../entityCollection';
import { createDataActions, createInitialActionsState, createInitialData, makeAction } from '../types';
import { replaceProcess } from './model';
import type { ProcessStore } from './types';
import { processActionNames } from './types';

const useProcessStoreBase = create<ProcessStore>()(
  withDevtools(
    (set, get) => ({
      data: createInitialData<ProcessType>(),
      isReady: false,
      _actionStates: createInitialActionsState(processActionNames),

      actions: {
        ...createDataActions<ProcessType, ProcessStore>(set),
        ...createEntityCollectionActions(
          { client: processClient, entityName: EntityName.processes },
          () => get().actions
        ),
        setItems: (items) =>
          set((state) => ({
            ...state,
            data: { items, loading: false },
            isReady: true,
          })),
        reset: () =>
          set((state) => ({
            ...state,
            data: { items: [], loading: true },
            isReady: false,
            _actionStates: createInitialActionsState(processActionNames),
          })),
      },

      asyncActions: {
        updateWorkflow: makeAction(
          set,
          'updateWorkflow',
          async (set, processId: string, workflowId: string, projectId: string) => {
            const saved = await processClient.updateWorkflow(processId, workflowId, projectId);
            set((state) => ({ ...state, data: { ...state.data, items: replaceProcess(state.data.items, saved) } }));
            return saved;
          }
        ),
      },
    }),
    StoreName.Processes
  )
);

export const useProcessStore = createSelectors(useProcessStoreBase);

export const getProcessStoreState = (): ProcessStore =>
  useProcessStore.getState();
